<template>
  <div>
    <!-- Legal basis table -->
    <div v-if="isGranted([permissions.PROCESS_LEGAL_BASIS_VIEW, permissions.PROCESS_LEGAL_BASIS_VIEW_OWN], process)"
         class="card legal_basis"
         qid="legal-basis-section"
         id="update_legal_basis"
    >
      <div class="card-body" :class="{'pr-0 pl-0': showOnTask}">
        <div class="row">
          <div class="col-sm-6">
            <div class="float-left">
              <h5 class="mb-2" qid="legal-basis-table-title" v-if="!showOnTask">
                {{ $t('processes.legal_basis.title') }}
              </h5>

              <span v-if="!showOnTask">
                <span class="cursor-pointer task-label-open mr-2"
                      v-if="getTask('update_legal_basis')"
                >
                  <UpdateSingleTaskModal
                      :task="getTask('update_legal_basis')"
                      @reloadContent="reloadContent"
                      :editIcon="true"
                  ></UpdateSingleTaskModal>
                </span>
                <span v-if="getGuide('update_legal_basis')">
                  <GuideButton :guide="getGuide('update_legal_basis')"></GuideButton>
                </span>
              </span>
            </div>
            <div class="validation-info" qid="view-lb-validation" v-if="process.legal_basis_validation && !showOnTask">
              <span v-if="process.legal_basis_validation && process.legal_basis_validation.validation === 1">
                <font-awesome-icon icon="check-circle" class="icon-success"/>
                {{ $t('system.approved') }}
              </span>
              <span v-if="process.legal_basis_validation && process.legal_basis_validation.validation === 0">
                <font-awesome-icon icon="times-circle" class="icon-danger"/>
                {{ $t('system.rejected') }}
              </span>
              <span v-if="process.legal_basis_validation" class="text-muted font-italic">
                {{ $t('system.by') }}
                <span v-if="process.legal_basis_validation.created_by" qid="view-tp-validation-by">
                  {{ process.legal_basis_validation.created_by.name }}
                </span>
              </span>
              <span v-if="process.legal_basis_validation" class="text-muted font-italic">
                {{ $t('system.on') }}
                <span v-if="process.legal_basis_validation.updated_at" qid="view-tp-validation-date">
                  {{ process.legal_basis_validation.updated_at | formatDate }}
                </span>
              </span>
            </div>
          </div>
          <div class="col-sm-6 text-right">
            <ValidationForm
                :object="process"
                type="legal_basis"
                scope="validate_legal_basis"
                :checklist="validationLBOptions"
                @validationModalClose="handleValidationModalClose"
                v-if="!showOnTask && isGranted([permissions.PROCESS_LEGAL_BASIS_VALIDATE, permissions.PROCESS_LEGAL_BASIS_VALIDATE_OWN], process)"
            ></ValidationForm>
            <span v-if="isGranted([permissions.PROCESS_LEGAL_BASIS_CREATE, permissions.PROCESS_LEGAL_BASIS_CREATE_OWN], process)"
                  class="btn btn-success"
                  qid="lb-create-button"
                  @click="showCreateLBModal"
            >
              <font-awesome-icon icon="plus"/> {{ $t('processes.legal_basis.create_button') }}
            </span>
          </div>
        </div>

        <table class="table table-responsive-md table-list-secondary">
          <thead>
          <tr>
            <th scope="col">
              {{ $t('processes.legal_basis.table.article') }}
            </th>
            <th scope="col">
              {{ $t('processes.legal_basis.table.name') }}
            </th>
            <th scope="col" class="text-center">
              {{ $t('processes.legal_basis.table.attachment') }}
            </th>
            <th scope="col">
              {{ $t('system.last_updated') }}
            </th>
            <th width="280"></th>
          </tr>
          </thead>
          <tbody>
            <tr v-for="object in lbTableItems">
              <td>
                <span v-if="object.article && object.article.name"
                      @click="openViewLBModal(object)"
                      qid="lb-name-link"
                      class="btn btn-link text-left cursor-pointer"
                >
                  {{ object.article.name }}
                </span>
              </td>
              <td>
                <span>
                  {{ object.name }}
                </span>
              </td>
              <td class="text-center">
                <span v-if="object.media && object.media.length">
                  <font-awesome-icon icon="paperclip"/>
                </span>
              </td>
              <td>
                <span class="text-normal">
                  {{ object.updated_at | formatDate }}
                </span>
              </td>
              <td align="right">
                <span v-if="isGranted([permissions.PROCESS_LEGAL_BASIS_EDIT, permissions.PROCESS_LEGAL_BASIS_EDIT_OWN], process)"
                      class="btn btn-secondary"
                      qid="edit-lb-modal"
                      @click="openEditLBModal(object)"
                >
                  {{ $t('system.reasoning') }}
                </span>
                <span v-if="object.article && object.article.name"
                      @click="openViewLBModal(object)"
                      qid="lb-name-link"
                      class="btn btn-view"
                >
                  <font-awesome-icon icon="eye"/>
                </span>
                <span v-if="isGranted([permissions.PROCESS_LEGAL_BASIS_DELETE, permissions.PROCESS_LEGAL_BASIS_DELETE_OWN], process)"
                      class="btn btn-delete"
                      qid="delete-lb-modal"
                      @click="showLBConfirmDeleteModal(object)"
                >
                  <font-awesome-icon icon="trash-alt"/>
                </span>
              </td>
            </tr>
            <tr v-if="!lbLoading && !lbTableItems.length" qid="no-results-row" class="text-center">
              <td colspan="10">
                <h5 class="mt-3">
                  {{ $t('system.no_results') }}
                </h5>
              </td>
            </tr>
          </tbody>
        </table>
        <br>
        <div class="text-center mb-4" v-if="!showOnTask">
          <span class="cursor-pointer"
                @click="lbValidationShow = !lbValidationShow"
                qid="expand-legal-basis-description"
          >
            <span class="mr-2">{{ $t('system.view_validation_log') }}</span>
            <font-awesome-icon icon="chevron-down" v-if="!lbValidationShow"/>
            <font-awesome-icon icon="chevron-up" v-if="lbValidationShow"/>
          </span>
        </div>
        <hr class="mb-0" v-show="lbValidationShow && !showOnTask">
        <ValidationList
            :object="process"
            type="legal_basis"
            :reloadList="updateValidationList"
            @validationListUpdated="setValidation"
            v-show="lbValidationShow && !showOnTask"
        ></ValidationList>
      </div>
    </div>


    <!-- Delete Legal basis modal template -->
    <b-modal ref="delete-lb" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-lb-title">
          {{ $t('processes.legal_basis.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('processes.legal_basis.delete.description') }}
        <span v-if="itemToDelete.article">{{ itemToDelete.article.name }}</span>
      </h5>
      <h5 class="text-center">
        {{ $t('processes.legal_basis.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideLBConfirmDeleteModal"
                  qid="delete-lb-cancel-button"
        >
          {{ $t('processes.legal_basis.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteLB(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-lb-submit-button"
                  v-if="isGranted([permissions.PROCESS_LEGAL_BASIS_DELETE, permissions.PROCESS_LEGAL_BASIS_DELETE_OWN], process)"
        >
          {{ $t('processes.legal_basis.delete.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Add new legal basis -->
    <b-modal ref="add-legal-basis" hide-footer @hide="hideCreateLBModal">
      <div class="d-block text-center">
        <h4 qid="add-lb-title">
          {{ $t('processes.legal_basis.create.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="create-lb-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="lbFormSubmit" qid="create-lb-form">
        <div class="form-container">
          <b-form-group>
            <label qid="create-lb-pg-label">
              {{ $t('processes.legal_basis.create.article_label') }}
            </label>
            <multiselect
                v-model="selectedArticle"
                :options="articles"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                :placeholder="$t('processes.legal_basis.create.article_placeholder')"
                label="name_modified"
                @search-change="loadArticles"
                @select="addLocalName"
                @remove="removeLocalName"
                track-by="slug"
                qid="create-lb-form-pg"
            ></multiselect>
          </b-form-group>
          <div v-if="selectedArticle && selectedArticle.description"
               class="text-center mb-3 mt-4"
          >
            <span class="cursor-pointer">
              <span class="mr-2"
                    @click="showArticleDescription = !showArticleDescription"
                    qid="expand-lb-description"
              >
                {{ $t('processes.legal_basis.view.description_expand') }}
              </span>
              <font-awesome-icon icon="chevron-down" v-if="!showArticleDescription"/>
              <font-awesome-icon icon="chevron-up" v-if="showArticleDescription"/>
            </span>
          </div>
          <div v-if="selectedArticle && selectedArticle.description && showArticleDescription" class="mb-4 mt-2">
            <span class="text-formatted">
              {{ selectedArticle.description }}
            </span>
          </div>
          <b-form-group>
            <label qid="create-lb-name-label">
              {{ $t('processes.legal_basis.create.name_label') }}
            </label>
            <b-form-input
              qid="create-lb-form-name"
              type="text"
              v-model="lbForm.name"
              :placeholder="$t('processes.legal_basis.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="create-lb-description-label">
              {{ $t('processes.legal_basis.create.reason_label') }}
            </label>
            <b-form-textarea
              qid="create-lb-form-description"
              v-model="lbForm.article_reason"
              rows="6"
              max-rows="6"
              :placeholder="$t('processes.legal_basis.create.reason_placeholder')"
            ></b-form-textarea>
          </b-form-group>
          <b-form-group>
            <label qid="create-tp-attachments-label">
              {{ $t('maintenance.third_parties.create.attachments') }}
              &nbsp;
              <span class="text-muted">({{ $t('attachments.file_size_label') }})</span>
            </label>
            <br>
            <p v-for="file in getFiles" :key="file.name">{{ file.name }}</p>
            <label class="btn btn-secondary cursor-pointer" qid="create-tp-attachments-button">
              <input type="file"
                     name="fileupload"
                     value="fileupload"
                     multiple
                     id="file-lb"
                     @change="onFileChange"
                     @click="resetFile"
              >
              <span>
                <font-awesome-icon icon="cloud-upload-alt" />
                {{ $t('system.upload') }}
              </span>
            </label>
          </b-form-group>
          <b-form-group>
            <label qid="create-tp-link-label">
              {{ $t('maintenance.third_parties.create.links_label') }}
            </label>
            <p v-for="(link, index) in lbForm.links">
              <span class="float-right text-muted cursor-pointer"
                    @click="removeLinkItem(index)"
                    qid="create-tp-link-remove"
              >
                <font-awesome-icon icon="trash-alt" />
              </span>
              <a :href="link.url" target="_blank" qid="create-p-link-item">{{ link.name ? link.name : link.url }}</a>
            </p>
            <div>
              <label class="mt-2">{{ $t('system.link') }}</label>
              <b-form-input
                type="text"
                aria-describedby="inputGroup"
                v-model="inputLink"
                :placeholder="$t('maintenance.third_parties.create.links_placeholder')"
                qid="create-p-links"
              ></b-form-input>
              <label class="mt-2">{{ $t('table.name') }}</label>
              <b-form-input
                type="text"
                aria-describedby="inputGroup"
                v-model="inputLinkName"
                :placeholder="inputLink ? inputLink : $t('table.name')"
                qid="create-p-links"
              ></b-form-input>
            </div>
            <span class="btn btn-link cursor-pointer float-right"
                  @click="handleLink"
                  qid="create-tp-add-link"
            >
              <font-awesome-icon icon="plus"/> {{ $t('system.add_link') }}
            </span>
          </b-form-group>

        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideCreateLBModal"
                    qid="create-lb-cancel-button"
          >
            {{ $t('processes.legal_basis.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    variant="success"
                    :disabled="buttonDisabled"
                    qid="create-lb-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('processes.legal_basis.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>


    <LegalBasisEditModal
        :object="selectedLB"
        :process="process"
        :showModal="editLBModal"
        :articles="articles"
        @closeEditModal="handleCloseEditLBModal"
    ></LegalBasisEditModal>

    <LegalBasisViewModal
        :object="selectedLB"
        :process="process"
        :showModal="viewLBModal"
        @closeViewModal="handleCloseViewLBModal"
    ></LegalBasisViewModal>


  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import Avatar from '@/components/Avatar.vue';
  import LegalBasisViewModal from '@/views/Processes/LegalBasisViewModal.vue';
  import LegalBasisEditModal from '@/views/Processes/LegalBasisEditModal.vue';
  import ValidationList from '@/views/Validations/ValidationList.vue';
  import ValidationForm from '@/views/Validations/ValidationForm.vue';
  import UpdateSingleTaskModal from '@/views/Tasks/UpdateSingleTaskModal.vue';
  import GuideButton from '@/components/guide/GuideButton.vue';


  export default {
    name: 'LegalBasisTable',
    data: function() {
      return {
        showFullDescription: false,
        showFullPurpose: false,
        process: {},
        selectedSort: '',
        selectedDirection: '',
        lbTableItems: [],
        lbLoading: true,
        lbValidationShow: false,

        itemToDelete: {},
        processGroups: [],
        tags: [],
        users: [],
        error: '',
        lbForm: {
          name: '',
          article_reason: '',
          links: []
        },
        inputLink: '',
        inputLinkName: '',
        selectedArticle: [],
        articles: [],
        selectedFiles: [],
        showArticleDescription: false,
        selectedLB: {},
        viewLBModal: false,
        editLBModal: false,
        updateValidationList: false,
        selectedLBSort: '',
        selectedLBDirection: '',
        showDescriptionInput: false,
        showPurposeInput: false,
        processEditDescription: '',
        processEditPurpose: '',
        validationIOOptions: [],
        validationLBOptions: [],
        buttonDisabled: false,
        myTasks: [],
        myGuides: [],
        tabIndex: 0,
        selectedItem: {},
        showTagsModal: false,
      }
    },
    props: {
      object: {
        type: Object,
        required: true
      },
      showOnTask: {},
      task: {}
    },
    components: {
      Avatar,
      LegalBasisViewModal,
      LegalBasisEditModal,
      ValidationList,
      ValidationForm,
      UpdateSingleTaskModal,
      GuideButton
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      getFiles() {
        return this.selectedFiles
      },
      getReloadContent() {
        return this.$store.state.updateContent
      },
      getReloadTasks() {
        return this.$store.state.reloadTasks
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {

      },
      object: function (value) {
        if (value && value.slug) {
          this.process = value
          this.validationLBOptions = _.cloneDeep(value.legal_basis_validation_options)
          this.loadLegalBasis();
          this.loadTasks('update_legal_basis');
        }
      },
      getReloadContent: function () {
        if (!this.showOnTask) {
          if (this.$store.state.updatedObject && this.$store.state.updatedObject.id) {
            if (this.$store.state.updatedObject.id == this.process.id && this.$store.state.updatedObject.who_am_i == this.process.who_am_i) {
              this.process = this.$store.state.updatedObject
            }
          }
          this.reloadContent()
        }
      },
      getReloadTasks: function () {
        if (!this.showOnTask) {
          this.loadTasks('update_legal_basis')
        }
      },
    },
    methods: {
      getTask(code) {
        let existingTask = _.find(this.myTasks, function(object) {
          return object.code == code
        });
        return existingTask
      },
      reloadContent() {
        this.loadLegalBasis()
        this.loadTasks('update_legal_basis');
      },
      setValidation(object) {
        this.process.legal_basis_validation = object
      },
      loadTasks(code) {
        let self = this;
        if (this.getClient.projectModule) {
          piincHttp.get('projects/me/tasks', { params:
            {
              per_page: 1,
              status: 'pending',
              model_type: this.process.who_am_i,
              model_id: this.process.id,
              code: code,
              client: this.getClient.slug
            }
          }).then(function(response) {
            self.myTasks = []
            if (response.data.items.length) {
              self.myTasks.push(response.data.items[0])
            }
          }, function() {});
        }
      },
      addLocalName(item) {
        this.lbForm.name = item.short_name;
      },
      removeLocalName(item) {
        this.lbForm.name = ''
      },
      handleValidationModalClose() {
        this.updateValidationList = true
        this.loadContent()
      },
      resetFile() {
        let imagefile = document.querySelector('#file-lb');
        imagefile.value = '';
      },
      onFileChange() {
        let files = document.querySelector('#file-lb');
        // in order for v-for to re render, we need to set array to empty
        this.selectedFiles = [];
        // And then push item by item into that array
        for (var i = 0; i < files.files.length; i++) {
          this.selectedFiles.push(files.files[i])
        }
      },
      handleLink() {
        // If there's a link in input, add it to links array
        if (this.inputLink !== '') {
          if (!this.inputLink.startsWith('http')) {
            this.inputLink = 'http://' + this.inputLink
          }
          this.lbForm.links.push({url: this.inputLink, name: this.inputLinkName})
          // Reset link input form
          this.inputLink = ''
          this.inputLinkName = ''
        }
      },
      removeLinkItem(index) {
        // Remove link item from the links array
        this.lbForm.links.splice(index, 1);
      },
      handleCloseViewLBModal () {
        this.loadLegalBasis();
        this.viewLBModal = false;
      },
      openViewLBModal(object) {
        this.selectedLB = object
        this.viewLBModal = true
      },
      openEditLBModal(object) {
        this.selectedLB = object
        this.editLBModal = true
      },
      handleCloseEditLBModal () {
        this.loadLegalBasis();
        this.editLBModal = false;
      },
      navigateToTask(object) {
        this.$store.commit('storeTaskToolbarState', true);
        this.$store.commit('storeActiveTask', object);
        this.$router.push({ name: 'processes.view', params: { slug: object.model.slug }, query: { task: object.id }})
      },
      openToolbar(object) {
        this.$store.commit('storeTaskToolbarState', true);
        this.$store.commit('storeActiveTask', object);
      },
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Process' && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Process',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      },
      showCreateLBModal() {
        this.loadArticles()
        this.$refs['add-legal-basis'].show()
      },
      hideCreateLBModal() {
        this.selectedArticle = [];
        this.lbForm.name = ''
        this.selectedFiles = []
        this.lbForm.article_reason = ''
        this.lbForm.links = []
        this.showArticleDescription = false;
        this.error = ''
        this.$refs['add-legal-basis'].hide()
      },
      showLBConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-lb'].show()
      },
      hideLBConfirmDeleteModal() {
        this.itemToDelete = {}
        this.$refs['delete-lb'].hide()
      },
      loadContent() {
        let self = this;
        piincHttp.get('processes/' + this.process.slug, { params:
          {
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.process = response.data;
        }, function() {});
      },
      confirmDeleteLB(object) {
        let self = this;
        piincHttp.delete('processes/' + this.process.slug + '/legal-basis/' + object.id).then(function() {
          self.hideLBConfirmDeleteModal();
          self.loadLegalBasis();
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('processes.legal_basis.delete.success'))
        }, function() {});
      },
      lbFormSubmit(evt) {
        let formData = new FormData();
        let files = document.querySelector('#file-lb');
        let sizeError = false;

        // Call handle link function to gather link from link input
        this.handleLink()
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        // Append list of attachments to FormData
        for (var i = 0; i < files.files.length; i++) {
          // Equal to 10 MB
          if (files.files[i].size > 10485760) {
            sizeError = true;
          }
          formData.append("files[]", files.files[i]);
        }

        // Append list of links to FormData
        for (var i = 0; i < this.lbForm.links.length; i++) {
          formData.append("links[]", JSON.stringify(this.lbForm.links[i]));
        }

        if (this.selectedArticle && this.selectedArticle.id) {
          formData.append("article_id", this.selectedArticle.id);
        }

        formData.append("name", this.lbForm.name);
        formData.append("article_reason", this.lbForm.article_reason);
        formData.append("process", this.process.slug);

        if (sizeError) {
          this.error = this.$t('system.file_size')
        } else {
          this.buttonDisabled = true;
          piincHttp.post('/processes/' + this.process.slug + '/legal-basis', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }).then(function(response) {
            self.$toastr('success', self.$t('processes.legal_basis.create.success'))
            self.hideCreateLBModal();
            self.buttonDisabled = false;
            self.loadLegalBasis();
            self.loadContent();
          }, function(error) {
            if (error.status !== 200) {
              self.buttonDisabled = false;
              self.error = error.data.message;
              if (error.data.errors.article_id) {
                self.error += ' ' + error.data.errors.article_id[0]
              }
              if (error.data.errors['files.0']) {
                self.error += ' ' + error.data.errors['files.0'][0]
              }
            }
          })
        }
      },
      // Table list
      loadLegalBasis() {
        let self = this;
        piincHttp.get('processes/' + this.process.slug + '/legal-basis', { params:
          {
            sort: this.selectedLBSort !== '' ? this.selectedLBSort : undefined,
            order: this.selectedLBDirection !== '' ? this.selectedLBDirection : undefined,
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.lbTableItems = response.data;
          self.lbLoading = false;
        }, function() {});
      },
      loadArticles(query) {
        let self = this;
        piincHttp.get('articles', { params:
          {
            client: this.getClient.slug,
            per_page: 100,
            keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.articles = []
          _.forEach(response.data.items, function (item) {
            item.name_modified = item.name + ' (' + item.short_name + ')'
            self.articles.push(item)
          })
          if (self.articles && !self.articles.length && !query) {
            self.articles.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
    },
    created () {
      this.process = this.object;
      if (this.getClient && this.getClient.slug) {
        this.loadArticles();
      }
      // Table items
      if (this.process && this.process.slug) {
        this.validationLBOptions = _.cloneDeep(this.process.legal_basis_validation_options)
        this.loadLegalBasis();
        this.loadTasks('update_legal_basis');
      }

      this.loadGuides('update_legal_basis');

    }
  }
</script>
